import { useMemo } from "react";

import BulkActionsDrawerItem from "components/BulkActions/Drawer/Item";
import BulkActionsDrawerItemDismiss from "components/BulkActions/Drawer/Item/Dismiss";
import BulkActionsDrawerItemLink from "components/BulkActions/Drawer/Item/Link";
import {
  BulkActionActionConfirmationTabs,
  BulkActionItemID,
  BulkActionsActionDescriptor,
  BulkActionsStep,
} from "components/BulkActions/types";
import Box from "ds/components/Box";
import useAnalytics from "hooks/useAnalytics";
import { ActionAvailabilityBanner } from "components/BulkActions/Drawer/Item/ActionAvailabiltyBanner";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { Space } from "components/icons/generated";
import Link from "ds/components/Link";
import useTypedContext from "hooks/useTypedContext";
import { SpacesContext } from "views/Account/SpacesProvider";
import Typography from "ds/components/Typography";

import { UserViewBinding } from "../../types";
import { UserRolesBulkActions } from "./types";

type UserRolesBulkActionsSelectedItemPropsBase = {
  item: UserViewBinding;
  onDismiss: (id: BulkActionItemID) => void;
};

type UserRolesBulkActionsSelectedItemProps = UserRolesBulkActionsSelectedItemPropsBase &
  (
    | {
        step: BulkActionsStep.ActionConfirmation;
        itemCategory: BulkActionActionConfirmationTabs;
        itemActions?: never;
        allActionsQty?: never;
      }
    | {
        step: BulkActionsStep.ChooseAction;
        itemActions: BulkActionsActionDescriptor<UserRolesBulkActions>[] | undefined;
        allActionsQty: number;
        itemCategory?: never;
      }
  );

const UserRolesBulkActionsSelectedItem = ({
  item,
  itemActions,
  onDismiss,
  allActionsQty,
  step,
  itemCategory,
}: UserRolesBulkActionsSelectedItemProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationUserRoles,
  });

  const { spaces } = useTypedContext(SpacesContext);

  const space = useMemo(
    () => spaces.find((space) => space.id === item.spaceID),
    [spaces, item.spaceID]
  );

  const handleDismiss = () => {
    onDismiss(item.id);
    trackSegmentAnalyticsEvent("Bulk Actions - Drawer - Item dismissed");
  };

  const isChooseActionStep = step === BulkActionsStep.ChooseAction;
  const actionTexts = (itemActions || []).map((action) => action.title);

  return (
    <BulkActionsDrawerItem key={item.id} direction="column" align="start">
      <Box gap="medium" align="start" justify="between" fullWidth zeroMinWidth={false}>
        <Box>
          <Box gap="large" direction="column" grow="1" fullWidth>
            {item.role.isSystem ? (
              <Typography tag="p" variant="p-body2">
                {item.role.name}
              </Typography>
            ) : (
              <BulkActionsDrawerItemLink
                link={`/settings/role/${item.role.id}`}
                text={item.role.name || ""}
                step={step}
                itemCategory={itemCategory}
              />
            )}

            {space && (
              <Link href={`/spaces/${space.id}`} startIcon={Space} variant="primary">
                {space.name}
              </Link>
            )}
          </Box>
        </Box>

        <BulkActionsDrawerItemDismiss onDismiss={handleDismiss} />
      </Box>

      {isChooseActionStep && (
        <ActionAvailabilityBanner itemActions={actionTexts} allActionsQty={allActionsQty} />
      )}
    </BulkActionsDrawerItem>
  );
};

export default UserRolesBulkActionsSelectedItem;
